import { useCallback, useMemo, useState } from "react";
import type { Dp, Filter } from "./ExState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton, Menu, MenuItem, Checkbox } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import UnitImage from "common/src/tft-static/UnitImage";
import ItemImage from "common/src/tft-static/ItemImage";
import { make as ItemName } from "common/src/tft-static/ItemName.bs";
import { make as SearchField } from "../components/SearchField2.bs";
import { parseSearch, searchUnitItems } from "../common/Search2";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs";
import { make as ItemTooltip } from "common/src/tft-static/ItemTooltip.bs";
import { common as t } from "common/src/bindings/Translation2";
import { use as useSet } from "common/src/data/TftSet.bs";
import { items } from "common/src/data/Data.bs";

const cols = [
  makeCol({
    width: 180,
    order: ([itemId], _, { itemsT }) => itemsT(itemId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Item")} </>,
    renderValue: ([unitId, itemId, _]) => (
      <div className="flex gap-[6px] items-center text-sm">
        {unitId === "" ? null : (
          <UnitTooltip unit={unitId}>
            <UnitImage unitId={unitId} size={27} />
          </UnitTooltip>
        )}
        <ItemTooltip itemId={itemId}>
          <ItemImage itemId={itemId} size={27} rounded="rounded" />
        </ItemTooltip>
        <div className="font-roboto font-normal truncate">
          <ItemName itemId={itemId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, __, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: ([_, __, v]) => <> {humanReadableCount(v.count) + " games"} </>,
    renderValue: ([_, __, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : (v.place / v.count).toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) => (hidePlacements ? 0 : v.delta),
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the item compared to the average placement of the filter combination without the item. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),
    renderValue: ([_, __, v], { hidePlacements }) => (
      <>
        {" "}
        {hidePlacements || !v.delta
          ? "—"
          : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)}{" "}
      </>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.top4 / v.count)} </>
    ),
  }),
  makeCol({
    width: 80,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.won / v.count)} </>
    ),
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: ([unitId, itemId], { onExpand, hideExpand }, { itemsT }) => {
      return hideExpand || unitId === "" ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) =>
              onExpand({
                typ: "i",
                id: itemId,
                sid: String(itemId),
                label: itemsT(itemId),
                unitId: [unitId, 0],
              })
            }
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  data: Array<[string, number, Dp]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  minSample: number;
  hidePlacements: boolean;
};

const ExItems = ({
  data,
  baseCount,
  onExpand,
  hideExpand,
  minSample,
  hidePlacements,
}: Props) => {
  const set = useSet();
  const [search, setSearch] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showCraftableItems, setShowCraftableItems] = useState(false);
  const [showOrnnItems, setShowOrnnItems] = useState(false);
  const [showEmblemItems, setShowEmblemItems] = useState(false);
  const [showRadiantItems, setShowRadiantItems] = useState(false);
  const [showSupportItems, setShowSupportItems] = useState(false);
  const filtersEnabled =
    !(
      showCraftableItems &&
      showOrnnItems &&
      showEmblemItems &&
      showRadiantItems &&
      showSupportItems
    ) &&
    (showCraftableItems ||
      showOrnnItems ||
      showEmblemItems ||
      showRadiantItems ||
      showSupportItems);

  const keyFn = useCallback(
    ([unitId, itemId, _]: [string, string, Dp]) => unitId + itemId,
    [],
  );
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand, hidePlacements }),
    [baseCount, onExpand, hideExpand, hidePlacements],
  );

  const visibleFilter = useMemo(() => {
    let passSearch: any;
    let passFilter: any;
    if (search === "") {
      passSearch = () => true;
    } else {
      const expr = parseSearch(search);
      passSearch = (unitId: string, itemId: string) =>
        searchUnitItems(expr, unitId === "" ? null : unitId, [itemId], set);
    }

    if (filtersEnabled) {
      passFilter = (itemId: string) => {
        const info = items(set)[itemId];
        return info.tags.some((tag) => {
          switch (tag) {
            case "Artifact":
              return showOrnnItems;
            case "Craftable":
              return showCraftableItems;
            case "CraftableEmblem":
              return showEmblemItems;
            case "Radiant":
              return showRadiantItems;
            case "ElusiveEmblem":
              return showEmblemItems;
            case "Support":
              return showSupportItems;
            default:
              return false;
          }
        });
      };
    } else {
      passFilter = () => true;
    }

    return ([unitId, itemId]: [string, string]) =>
      passSearch(unitId, itemId) && passFilter(itemId);
  }, [
    search,
    showOrnnItems,
    showCraftableItems,
    showEmblemItems,
    showRadiantItems,
    showSupportItems,
  ]);

  const filteredData = useMemo(
    () => data.filter(([_, _1, v]) => v.count >= minSample),
    [data, minSample],
  );

  if (data.length === 0) {
    return (
      <div className="px-5 py-6 text-xl text-medium text-white2">
        No item combos with at least 100 games played
      </div>
    );
  } else {
    return (
      <>
        <div className="p-2 flex justify-between">
          <SearchField search={search} setSearch={setSearch} />
          <div
            className="mr-4 cursor-pointer"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <FilterAltIcon color={filtersEnabled ? "primary" : undefined} />
          </div>
        </div>
        <div>
          <StatsTable
            cols={cols}
            context={context}
            data={filteredData}
            keyFn={keyFn}
            stickyColCount={1}
            defaultSortIdx={1}
            top="t47m92"
            visibleFilter={visibleFilter}
          />
        </div>
        <Menu
          open={menuAnchor !== null}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => setShowCraftableItems((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showCraftableItems} />
            {t("Craftable")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowOrnnItems((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showOrnnItems} />
            {t("Ornn")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowEmblemItems((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showEmblemItems} />
            {t("Emblem")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowRadiantItems((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showRadiantItems} />
            {t("Radiant")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowSupportItems((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showSupportItems} />
            {t("Support")}
          </MenuItem>
        </Menu>
      </>
    );
  }
};

export default ExItems;
